import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import {
    Currency,
    JobPostDTO,
    SimpleJobPostDTO,
    JobPeriod,
    Modality,
    Frequency,
} from '../../models/post'
import CustomInput from '../TextInput'
import { IconButton } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import CustomButton from '../Button'
import CustomDropdown from '../Dropdown'
import JobService from '../../services/JobService'
import { enqueueSnackbar } from 'notistack'
import CustomDatePicker from '../DatePicker'
import {
    dateFormatter,
    translateEnumFrequencyToString,
    translateEnumJobPeriodToString,
    translateEnumModalityToString,
    translateStringToEnumFrequency,
    translateStringToEnumJobPeriod,
    translateStringToEnumModality,
    validateEmail,
    validateNumber,
} from '../../helpers/utils'
import AvatarUploader, { AvatarPlaceholder } from '../../app/register/avatarUploader/AvatarUploader'
import CustomModal from '../../containers/Modal'
import { FetchResponse } from '../../models/shared'
import dayjs from 'dayjs'
import EditIcon from '@mui/icons-material/Edit'
import SearchAbilitiesModal from '../../components/Modal/SearchAbilitiesModal'
import { Chip } from '@mui/material'
import CustomIconButton from '../IconButton'

interface Props {
    info?: JobPostDTO
    handleClose: () => void
    setMutate?: Dispatch<SetStateAction<boolean>>
}

const DEFAULT_POST: SimpleJobPostDTO = {
    id: '0',
    title: '',
    description: '',
    companyLogo: '',
    company: '',
    location: '',
    modality: Modality.IN_PERSON,
    currency: Currency.ARS,
    frequency: Frequency.HOURLY,
    amount: '',
    jobPeriod: JobPeriod.INTERNSHIP,
    finishDate: '',
    abilities: [],
    contact: '',
    active: true,
    applicants: [],
}

const DEFAULT_ERRORS = {
    title: '',
    description: '',
    companyLogo: '',
    company: '',
    location: '',
    amount: '',
    finishDate: '',
    abilities: '',
    contact: '',
}

export const currencyOptions = [
    Currency.ARS,
    Currency.BRL,
    Currency.EUR,
    Currency.MXN,
    Currency.USD,
]

export const workTypeOptions = [JobPeriod.INTERNSHIP, JobPeriod.PART_TIME, JobPeriod.FULL_TIME].map(
    (job) => translateEnumJobPeriodToString(job),
)

export const modalityOptions = [Modality.IN_PERSON, Modality.REMOTE, Modality.HYBRID].map(
    (modality) => translateEnumModalityToString(modality),
)

export const frequencyOptions = [
    Frequency.HOURLY,
    Frequency.WEEKLY,
    Frequency.MONTHLY,
    Frequency.YEARLY,
].map((frequency) => translateEnumFrequencyToString(frequency))

const JobPostModal = ({ info, handleClose, setMutate = () => {} }: Props) => {
    const [post, setPost] = useState<SimpleJobPostDTO>(DEFAULT_POST)
    const [errors, setErrors] = useState<typeof DEFAULT_ERRORS>(DEFAULT_ERRORS)
    const [isAvatarUploadOpen, setIsAvatarUploadOpen] = useState<boolean>(false)
    const [isAbilitiesModalOpen, setIsAbilitiesModalOpen] = useState<boolean>(false)

    useEffect(() => {
        if (info) {
            const [day, month, year] = info.finishDate.split('/')

            const formattedDate = new Date(`${year}-${month}-${day}`)
            const isValidDate = !isNaN(formattedDate.getTime())

            setPost(
                new SimpleJobPostDTO({
                    ...info,
                    finishDate: isValidDate ? dateFormatter(formattedDate) : '',
                }),
            )
        }
    }, [info])

    const handleOnChange = (name: string, value: string, files?: FileList) => {
        if (files && files[0]) {
            const file = files[0]
            const reader = new FileReader()

            reader.onloadend = () => {
                const base64String = reader.result as string

                setPost((prev) => ({
                    ...prev,
                    image: base64String,
                }))
            }

            reader.readAsDataURL(file)
        } else {
            setPost((prev) => ({ ...prev, [name]: value }))
        }
    }

    const disableSubmit = () =>
        Object.entries(post).some((x) => x[0] !== 'amount' && (x[1] === '' || x[1] === null)) ||
        post.abilities.length === 0

    const validateInfo = () => {
        const newErrors = {
            companyLogo: post.companyLogo === '' ? 'Falta logo' : '',
            company: post.company === '' ? 'Completá este campo' : '',
            location: post.location === '' ? 'Completá este campo' : '',
            title: post.title === '' ? 'Completá este campo' : '',
            description: post.description === '' ? 'Completá este campo' : '',
            amount:
                post.amount !== '' && !validateNumber(post.amount) ? 'El monto no es válido' : '',
            finishDate: post.finishDate === '' ? 'Completá este campo' : '',
            abilities: post.abilities.length === 0 ? 'Completá este campo' : '',
            contact:
                post.contact === ''
                    ? 'Completá este campo'
                    : !validateEmail(post.contact)
                      ? 'El email de contacto ingresado no es válido'
                      : '',
        }

        setErrors(newErrors)

        return Object.values(newErrors).every((x) => x === '')
    }

    const handleSubmit = async () => {
        if (!validateInfo()) return

        let response: FetchResponse

        if (info) {
            response = await new JobService().editJobPost(info.id, post)
        } else {
            response = await new JobService().createJobPost(post)
        }

        if (response.status === 200) {
            let message: string
            if (info) {
                message = 'Oferta Laboral editada'
            } else {
                message = 'Oferta Laboral creada'
            }

            setMutate((prevMutate) => !prevMutate)

            enqueueSnackbar(message, {
                variant: 'success',
                autoHideDuration: 6000,
            })
        } else {
            enqueueSnackbar('Error inesperado', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        }

        handleClose()
    }

    return (
        <>
            <div className="flex flex-col w-[65vw] md:w-[35vw] overflow-auto max-h-[50vh] md:max-h-[65vh] px-0.5 md:px-2 gap-3 md:gap-4 text-error">
                <div className="flex justify-between items-center">
                    <label
                        className="text-secondary font-semibold"
                        htmlFor="postInput"
                    >
                        {info ? 'Editar Oferta Laboral' : 'Nueva Oferta Laboral'}
                    </label>
                    <div className="flex items-center">
                        <IconButton
                            className="text-primary"
                            onClick={handleClose}
                        >
                            <ClearIcon />
                        </IconButton>
                    </div>
                </div>
                <CustomInput
                    label="Título"
                    name="title"
                    onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                    value={post.title}
                    errorText={errors.title}
                    error={errors.title !== ''}
                />
                <CustomInput
                    label="Descripción"
                    name="description"
                    onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                    value={post.description}
                    errorText={errors.description}
                    error={errors.description !== ''}
                    className="w-full border"
                    multiline
                    minRows={3}
                />
                <div className="flex items-center gap-2 md:gap-4 h-full">
                    <div className="flex flex-col w-1/3 md:w-1/4 items-center gap-2">
                        <span className="text-center text-primary text-sm md:text-base">Logo</span>
                        <div
                            className={`${errors.companyLogo !== '' ? 'w-4/5 h-4/5' : 'w-full h-full'} aspect-square rounded-full outline outline-primary -outline-offset-1 outline-[2px] cursor-pointer`}
                            onClick={() => {
                                setIsAvatarUploadOpen(true)
                            }}
                        >
                            {post.companyLogo !== '' ? (
                                <img
                                    width="100%"
                                    height="100%"
                                    alt="companyLogo"
                                    src={post.companyLogo}
                                    className="rounded-full"
                                />
                            ) : (
                                <AvatarPlaceholder />
                            )}
                        </div>
                        {errors.companyLogo !== '' && <span>{errors.companyLogo}</span>}
                    </div>
                    <div className="flex flex-col gap-2 md:gap-4 w-full">
                        <CustomInput
                            label="Empresa"
                            name="company"
                            onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                            value={post.company}
                            errorText={errors.company}
                            error={errors.company !== ''}
                            className="w-full border"
                        />
                        <CustomInput
                            label="Ubicación"
                            name="location"
                            onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                            value={post.location}
                            errorText={errors.location}
                            error={errors.location !== ''}
                            className="w-full border"
                        />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
                    <CustomDropdown
                        className="col-span-1"
                        label="Modalidad"
                        name="modality"
                        value={translateEnumModalityToString(post.modality) ?? ''}
                        onChange={(e) =>
                            handleOnChange(
                                e.target.name,
                                translateStringToEnumModality(e.target.value as string),
                            )
                        }
                        data={modalityOptions}
                    />
                    <CustomDatePicker
                        label="Fecha límite"
                        name="finishDate"
                        value={post.finishDate}
                        onChange={handleOnChange}
                        minValue={dayjs(Date.now())}
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                    <CustomDropdown
                        className="col-span-1"
                        label="Tipo de Moneda"
                        name="currency"
                        value={post.currency ?? ''}
                        onChange={(e) => handleOnChange(e.target.name, e.target.value as string)}
                        data={currencyOptions}
                    />
                    <CustomDropdown
                        className="col-span-1"
                        label="Jornada Laboral"
                        name="jobPeriod"
                        value={translateEnumJobPeriodToString(post.jobPeriod) ?? ''}
                        onChange={(e) => {
                            handleOnChange(
                                e.target.name,
                                translateStringToEnumJobPeriod(e.target.value as string),
                            )
                        }}
                        data={workTypeOptions}
                    />
                    <CustomDropdown
                        className="col-span-1"
                        label="Frecuencia de pago"
                        name="frequency"
                        value={translateEnumFrequencyToString(post.frequency) ?? ''}
                        onChange={(e) => {
                            handleOnChange(
                                e.target.name,
                                translateStringToEnumFrequency(e.target.value as string),
                            )
                        }}
                        data={frequencyOptions}
                    />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    <CustomInput
                        className="w-full border col-span-1"
                        label="Salario aproximado (opcional)"
                        name="amount"
                        onChange={(e) => {
                            handleOnChange(e.target.name, e.target.value)
                        }}
                        value={post.amount}
                        errorText={errors.amount}
                        error={errors.amount !== ''}
                    />
                    <CustomInput
                        className="w-full border col-span-1"
                        label="Email de contacto"
                        name="contact"
                        onChange={(e) => handleOnChange(e.target.name, e.target.value)}
                        value={post.contact}
                        errorText={errors.contact}
                        error={errors.contact !== ''}
                    />
                </div>
                <div className="flex gap-1 md:gap-2 items-center text-[#777777] ml-1 md:mb-2 md:mt-3">
                    <span className="text-sm md:text-base">Aptitudes deseadas</span>
                    <CustomIconButton
                        className="h-fit text-gray"
                        icon={<EditIcon />}
                        onClick={() => setIsAbilitiesModalOpen(true)}
                    />
                </div>
                {post.abilities && post.abilities.length > 0 ? (
                    <div className="flex gap-1 items-center px-0.5 md:px-0 flex-wrap ml-1 md:mt-1 md:mb-1">
                        {post.abilities.map((ability) => (
                            <div
                                className="w-full"
                                key={`selected${ability}`}
                            >
                                <Chip label={ability} />
                            </div>
                        ))}
                    </div>
                ) : (
                    <span className="ml-1 text-[#777777] text-xs md:text-base ">
                        Seleccioná las aptitudes o habilidades deseadas para el puesto.
                    </span>
                )}
                <CustomButton
                    text={info ? 'Editar' : 'Publicar'}
                    onClick={handleSubmit}
                    disabled={disableSubmit()}
                />
            </div>
            <CustomModal
                isOpen={isAvatarUploadOpen}
                handleClose={() => setIsAvatarUploadOpen(false)}
            >
                <AvatarUploader
                    handleClose={() => setIsAvatarUploadOpen(false)}
                    setAvatarImage={(image?: string) => handleOnChange('companyLogo', image!)}
                />
            </CustomModal>
            <CustomModal
                isOpen={isAbilitiesModalOpen}
                handleClose={() => setIsAbilitiesModalOpen(false)}
            >
                <SearchAbilitiesModal
                    text="Seleccionar aptitudes"
                    handleClose={() => setIsAbilitiesModalOpen(false)}
                    selectedAbilities={post.abilities ?? []}
                    setSelectedAbility={(ability: string) =>
                        setPost((prev) => ({
                            ...prev,
                            abilities: prev.abilities?.includes(ability)
                                ? prev.abilities.filter((x) => x !== ability)
                                : [...(prev.abilities ?? []), ability],
                        }))
                    }
                />
            </CustomModal>
        </>
    )
}

export default JobPostModal
